// countryNorm.js — country name ↔ ISO-2 normalization
//
// Why this exists: the IP enrichment table (silver_m365_unified_ip_enrichment)
// stores country_name as the full English name ("United States"), while the
// baseline tables (silver_m365_7_day_normal etc.) store country_list as
// ISO-3166 alpha-2 codes ("US"). Both feed into the Baseline Comparison
// panel for the Country row. Without normalization, "United States" never
// matches "US" and the row paints red on every classic-signin alert (which
// has no rawEvent.location.countryOrRegion to fall back to either).
//
// This file is a portal-side bridge. The proper long-term fix is for the
// pipeline to write a single canonical column shape on both sides — see
// M365Signal-Databricks-Pipelines/issues/2026-05-21_country-format-mismatch.md.
//
// Canonical form: uppercase ISO-3166 alpha-2 (US, GB, CA, ...).
// Strings ≤ 3 chars are assumed to already be ISO codes and just uppercased.
// Anything else gets looked up in COUNTRY_NAME_TO_ISO; unknown names fall
// through as uppercased originals so we never silently lose data.

const COUNTRY_NAME_TO_ISO = Object.freeze({
  // M365 high-traffic countries — covers ~98% of what shows up in alerts
  'united states': 'US',
  'united states of america': 'US',
  'usa': 'US',
  'united kingdom': 'GB',
  'great britain': 'GB',
  'england': 'GB',
  'canada': 'CA',
  'mexico': 'MX',
  'philippines': 'PH',
  'india': 'IN',
  'australia': 'AU',
  'new zealand': 'NZ',
  'germany': 'DE',
  'france': 'FR',
  'ireland': 'IE',
  'netherlands': 'NL',
  'belgium': 'BE',
  'luxembourg': 'LU',
  'switzerland': 'CH',
  'austria': 'AT',
  'spain': 'ES',
  'portugal': 'PT',
  'italy': 'IT',
  'greece': 'GR',
  'sweden': 'SE',
  'norway': 'NO',
  'finland': 'FI',
  'denmark': 'DK',
  'iceland': 'IS',
  'poland': 'PL',
  'czech republic': 'CZ',
  'czechia': 'CZ',
  'slovakia': 'SK',
  'hungary': 'HU',
  'romania': 'RO',
  'bulgaria': 'BG',
  'croatia': 'HR',
  'slovenia': 'SI',
  'serbia': 'RS',
  'bosnia and herzegovina': 'BA',
  'montenegro': 'ME',
  'north macedonia': 'MK',
  'albania': 'AL',
  'estonia': 'EE',
  'latvia': 'LV',
  'lithuania': 'LT',
  'ukraine': 'UA',
  'belarus': 'BY',
  'moldova': 'MD',
  'russia': 'RU',
  'russian federation': 'RU',
  'turkey': 'TR',
  'türkiye': 'TR',
  'cyprus': 'CY',
  'malta': 'MT',
  'israel': 'IL',
  'palestine': 'PS',
  'jordan': 'JO',
  'lebanon': 'LB',
  'saudi arabia': 'SA',
  'united arab emirates': 'AE',
  'qatar': 'QA',
  'bahrain': 'BH',
  'kuwait': 'KW',
  'oman': 'OM',
  'iraq': 'IQ',
  'iran': 'IR',
  'iran, islamic republic of': 'IR',
  'egypt': 'EG',
  'south africa': 'ZA',
  'nigeria': 'NG',
  'kenya': 'KE',
  'morocco': 'MA',
  'tunisia': 'TN',
  'algeria': 'DZ',
  'ghana': 'GH',
  'ethiopia': 'ET',
  'china': 'CN',
  "china, people's republic of": 'CN',
  'hong kong': 'HK',
  'macau': 'MO',
  'taiwan': 'TW',
  'taiwan, province of china': 'TW',
  'japan': 'JP',
  'south korea': 'KR',
  'korea': 'KR',
  'korea, republic of': 'KR',
  'north korea': 'KP',
  'singapore': 'SG',
  'malaysia': 'MY',
  'indonesia': 'ID',
  'thailand': 'TH',
  'vietnam': 'VN',
  'viet nam': 'VN',
  'cambodia': 'KH',
  'laos': 'LA',
  'myanmar': 'MM',
  'burma': 'MM',
  'pakistan': 'PK',
  'bangladesh': 'BD',
  'sri lanka': 'LK',
  'nepal': 'NP',
  'afghanistan': 'AF',
  'kazakhstan': 'KZ',
  'uzbekistan': 'UZ',
  'azerbaijan': 'AZ',
  'georgia': 'GE',
  'armenia': 'AM',
  'brazil': 'BR',
  'argentina': 'AR',
  'chile': 'CL',
  'colombia': 'CO',
  'peru': 'PE',
  'venezuela': 'VE',
  'ecuador': 'EC',
  'uruguay': 'UY',
  'paraguay': 'PY',
  'bolivia': 'BO',
  'guatemala': 'GT',
  'costa rica': 'CR',
  'panama': 'PA',
  'el salvador': 'SV',
  'honduras': 'HN',
  'nicaragua': 'NI',
  'dominican republic': 'DO',
  'cuba': 'CU',
  'puerto rico': 'PR',
  'jamaica': 'JM',

  // Extended 2026-05-22 from the ProxyCheck gap-analysis handoff
  // (Issues/2026-05-22_countryNorm-extension-and-retirement.md). These names
  // are ones ProxyCheck v3 emits verbatim that the section above didn't cover.
  // Aliases included for ISO long-form spellings + historical names where
  // they're worth pre-empting.
  'the netherlands': 'NL',
  'u.s. virgin islands': 'VI',
  'us virgin islands': 'VI',
  'virgin islands, u.s.': 'VI',
  'gambia': 'GM',
  'gambia, the': 'GM',
  'bahamas': 'BS',
  'bahamas, the': 'BS',
  'turks and caicos islands': 'TC',
  'tanzania': 'TZ',
  'tanzania, united republic of': 'TZ',
  'mozambique': 'MZ',
  'cayman islands': 'KY',
  'uganda': 'UG',
  'trinidad and tobago': 'TT',
  'syria': 'SY',
  'syrian arab republic': 'SY',
  'aruba': 'AW',
  'barbados': 'BB',
  'cameroon': 'CM',
  'malawi': 'MW',
  'eswatini': 'SZ',
  'swaziland': 'SZ',
  'brunei': 'BN',
  'brunei darussalam': 'BN',
  'cabo verde': 'CV',
  'cape verde': 'CV',
  'zambia': 'ZM',
  'ivory coast': 'CI',
  "côte d'ivoire": 'CI',
  'libya': 'LY',
  'zimbabwe': 'ZW',
  'kyrgyzstan': 'KG',
  'republic of the congo': 'CG',
  'congo': 'CG',
  'democratic republic of the congo': 'CD',
  'liberia': 'LR',
});

/**
 * Normalize a country token (full name OR ISO code) to canonical uppercase ISO-2.
 * Unknown long names are returned uppercased verbatim so we never lose data.
 */
export function normalizeCountry(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  if (trimmed.length <= 3) return trimmed.toUpperCase();
  const iso = COUNTRY_NAME_TO_ISO[trimmed.toLowerCase()];
  return iso || trimmed.toUpperCase();
}

// ── Alert-time Baseline Comparison country rule (v1.193.0) ──────────────────────────────────────────
// A VERBATIM port of the pipeline's `_bc_normalize_country` (M365Signal-Databricks-Pipelines
// notebooks/_detection_lib.py: `_COUNTRY_CODE_TO_NAME`, `_BC_COUNTRY_ALIASES`). The pipeline stores its
// Country verdict in `baseline_comparison_json`; the portal re-evaluates the Country row ONLY after an
// analyst's IP-enrichment refresh, and must reach the same verdict on the same input, so this is the
// pipeline's table, not ours (ours above maps e.g. 'croatia' -> HR where the pipeline keeps 'CROATIA').
// Deliberately separate from normalizeCountry, which other surfaces still use. A test compares these tables
// with the Python source when that repo is present. Change them only together with the pipeline.
const BC_COUNTRY_CODE_TO_NAME = Object.freeze({
  AE: 'United Arab Emirates', AF: 'Afghanistan', AG: 'Antigua and Barbuda',
  AL: 'Albania', AM: 'Armenia', AO: 'Angola', AR: 'Argentina',
  AT: 'Austria', AU: 'Australia', AW: 'Aruba', AZ: 'Azerbaijan',
  BA: 'Bosnia and Herzegovina', BB: 'Barbados', BD: 'Bangladesh',
  BE: 'Belgium', BG: 'Bulgaria', BH: 'Bahrain', BI: 'Burundi',
  BN: 'Brunei', BR: 'Brazil', BS: 'Bahamas', BY: 'Belarus',
  CA: 'Canada', CG: 'Republic of the Congo', CH: 'Switzerland',
  CL: 'Chile', CM: 'Cameroon', CN: 'China', CO: 'Colombia',
  CR: 'Costa Rica', CV: 'Cabo Verde', CY: 'Cyprus', CZ: 'Czechia',
  DE: 'Germany', DK: 'Denmark', DO: 'Dominican Republic', DZ: 'Algeria',
  EC: 'Ecuador', EE: 'Estonia', EG: 'Egypt', ES: 'Spain',
  ET: 'Ethiopia', FI: 'Finland', FR: 'France', GB: 'United Kingdom',
  GE: 'Georgia', GH: 'Ghana', GM: 'Gambia', GR: 'Greece',
  HK: 'Hong Kong', HU: 'Hungary', ID: 'Indonesia', IE: 'Ireland',
  IL: 'Israel', IM: 'Isle of Man', IN: 'India', IQ: 'Iraq',
  IS: 'Iceland', IT: 'Italy', JM: 'Jamaica', JP: 'Japan',
  KE: 'Kenya', KG: 'Kyrgyzstan', KH: 'Cambodia', KR: 'South Korea',
  KW: 'Kuwait', KZ: 'Kazakhstan', LA: 'Laos', LB: 'Lebanon',
  LS: 'Lesotho', LT: 'Lithuania', LU: 'Luxembourg', LV: 'Latvia',
  LY: 'Libya', MA: 'Morocco', MC: 'Monaco', MD: 'Moldova',
  ME: 'Montenegro', MK: 'North Macedonia', MM: 'Myanmar', MW: 'Malawi',
  MX: 'Mexico', MY: 'Malaysia', MZ: 'Mozambique', NA: 'Namibia',
  NG: 'Nigeria', NI: 'Nicaragua', NL: 'The Netherlands', NO: 'Norway',
  NP: 'Nepal', NZ: 'New Zealand', OM: 'Oman', PE: 'Peru',
  PH: 'Philippines', PK: 'Pakistan', PL: 'Poland', PS: 'Palestinian Territory',
  PT: 'Portugal', QA: 'Qatar', RE: 'Reunion', RO: 'Romania',
  RS: 'Serbia', RU: 'Russia', SA: 'Saudi Arabia', SC: 'Seychelles',
  SE: 'Sweden', SG: 'Singapore', SK: 'Slovakia', SO: 'Somalia',
  SZ: 'Eswatini', TD: 'Chad', TH: 'Thailand', TJ: 'Tajikistan',
  TM: 'Turkmenistan', TN: 'Tunisia', TR: 'Turkey', TT: 'Trinidad and Tobago',
  TW: 'Taiwan', TZ: 'Tanzania', UA: 'Ukraine', UG: 'Uganda',
  US: 'United States', UZ: 'Uzbekistan', VE: 'Venezuela',
  VI: 'U.S. Virgin Islands', VN: 'Vietnam', YE: 'Yemen',
  ZA: 'South Africa', ZM: 'Zambia', ZW: 'Zimbabwe',
});
const BC_COUNTRY_ALIASES = Object.freeze({
  'USA': 'US', 'UNITED STATES OF AMERICA': 'US', 'UK': 'GB', 'GBR': 'GB', 'GREAT BRITAIN': 'GB',
  'ENGLAND': 'GB', 'CAN': 'CA', 'MEX': 'MX', 'PHL': 'PH', 'IND': 'IN', 'NETHERLANDS': 'NL',
  'CZECH REPUBLIC': 'CZ', 'RUSSIAN FEDERATION': 'RU', 'KOREA': 'KR', 'REPUBLIC OF KOREA': 'KR',
  'IVORY COAST': 'CI', 'CÔTE D\'IVOIRE': 'CI', 'COTE D\'IVOIRE': 'CI', 'BRUNEI DARUSSALAM': 'BN',
  'CAPE VERDE': 'CV', 'CONGO': 'CG', 'DEMOCRATIC REPUBLIC OF THE CONGO': 'CD', 'LIBERIA': 'LR',
  'VIET NAM': 'VN', 'TÜRKIYE': 'TR', 'TURKIYE': 'TR', 'HONG KONG SAR': 'HK', 'PALESTINE': 'PS',
});
const BC_COUNTRY_NAME_TO_CODE = Object.freeze(Object.fromEntries(
  Object.entries(BC_COUNTRY_CODE_TO_NAME).map(([code, name]) => [name.toUpperCase(), code]),
));
export const BC_COUNTRY_TABLES = { codeToName: BC_COUNTRY_CODE_TO_NAME, aliases: BC_COUNTRY_ALIASES };

/** Canonical ISO-2 for a country name OR code, exactly as the pipeline's `_bc_normalize_country`. */
export function normalizeCountryIso2(value) {
  if (value == null) return null;
  const t = String(value).trim();
  if (!t) return null;
  const u = t.toUpperCase();
  if (/^\p{L}{2}$/u.test(u)) return u;
  return BC_COUNTRY_ALIASES[u] || BC_COUNTRY_NAME_TO_CODE[u] || u;
}

/** The pipeline's `_bc_match_country`: true / false / null (nothing to compare). */
export function matchCountryListIso2(eventValue, baselineCsv) {
  if (eventValue == null || !baselineCsv) return null;
  const ev = normalizeCountryIso2(eventValue);
  if (!ev) return null;
  const list = String(baselineCsv).split(',').map(normalizeCountryIso2).filter(Boolean);
  return list.length ? list.includes(ev) : null;
}

/**
 * Return true if `eventValue` matches any item in the comma-separated baseline
 * list, comparing in canonical ISO form so "United States" matches "US".
 *
 * Returns:
 *   true  — event value normalizes to one of the baseline items
 *   false — event value normalizes to something NOT in the baseline
 *   null  — no event value or no baseline data to compare against
 */
export function matchCountryList(eventValue, baselineCsv) {
  if (eventValue == null || !baselineCsv) return null;
  const ev = normalizeCountry(eventValue);
  if (!ev) return null;
  const list = String(baselineCsv)
    .split(',')
    .map(s => normalizeCountry(s))
    .filter(Boolean);
  if (list.length === 0) return null;
  return list.includes(ev);
}
